import * as utils from '@/common/utils'
import * as sse from '@/common/sse'
import ApiService from '@/common/api.service'
import axios from 'axios'

let audioInstance = null
let soundMuteTimeout = null

/**
 * Returns all properties and their default state.
 *
 * @return Object
 */
function properties () {
  return {
    listenToPool: {},
    listenToPoolId: null,
    listenToPoolName: null,
    listenToToken: null,
    soundPlaying: false,
    soundMuted: false,
    monitorMode: 'pool-selection',
    events: [],
    eventCounters: []
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    ...properties()
  }
}

/**
 * Returns a copy of the default form.
 *
 * @return Object
 */
function formDefaults () {
  return {
  }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  isModified: false,
  isLoading: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  },

  getListenToPoolId (state) {
    return state.data.listenToPoolId
  },

  getListenToPoolName (state) {
    return state.data.listenToPoolName
  },

  getSoundPlaying (state) {
    return state.data.soundPlaying
  },

  getSoundMuted (state) {
    return state.data.soundMuted
  },

  getListenToPool (state) {
    return state.data.listenToPool
  },

  getListenToToken (state) {
    return state.data.listenToToken
  },

  getMonitorMode (state) {
    return state.data.monitorMode
  },

  getEvents (state) {
    return state.data.events
  },

  getEventCounters (state) {
    return state.data.eventCounters
  }
}

export const actions = {
  /**
   * Called when the SSE stream is ready to be used. Subscribe to the
   * listenToPoolId if it is not null.
   *
   * @param context Object
   */
  handleSseReady (context) {
    const listenToPoolId = context.getters['getListenToPoolId']
    if (listenToPoolId !== null) {
      return ApiService
        .get(`monitoring/pool/${listenToPoolId}`)
        .then((response) => {
          const pool = response.data.data
          context.commit('setListenToPoolName', pool.name)
          context.commit('setListenToToken', pool.token)
          return context.dispatch('postEventSubscribe')
        })
    } else {
      return new Promise((resolve) => {
        resolve()
      })
    }
  },

  /**
   * Subscribes to the SSE server and selected pool id.
   *
   * @param context Object
   */
  postEventSubscribe (context) {
    const poolToken = context.getters['getListenToToken']
    const sseUrl = sse.getSseUrl()
    const sseStreamId = sse.getStreamId()
    const url = `${sseUrl}events/subscribe/${sseStreamId}/${poolToken}`

    // Submit the request to the SSE server.
    return axios.post(url, {}, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
  },

  /**
   * Unsubscribes from listening to the current pool.
   *
   * @param context Object
   */
  postEventUnsubscribe (context) {
    const sseUrl = sse.getSseUrl()
    const sseStreamId = sse.getStreamId()
    const poolToken = context.getters['getListenToToken']
    const url = `${sseUrl}events/unsubscribe/${sseStreamId}/${poolToken}`

    // Submit the request to the SSE server.
    return axios.post(url, {}, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
  },

  /**
   * Returns an object with some details about the user's accessible
   * pools. The object's "pools" property specifies how many pools the
   * user can access, with 'none' being no pools, 'one' being 1 pool
   * can be accessed and 'multiple' being more than 1 pool can be
   * accessed. If only one pool can be accessed, the "id" property contains
   * the id of the pool, else it will be set to null.
   *
   * @param context Object
   */
  getPoolAccessStatus (context) {
    return ApiService
      .get(`events/pools`, { per_page: 2 })
      .then((response) => {
        if (response.data.data.length === 0) {
          return { pools: 'none', pool: null }
        } else if (response.data.data.length === 1) {
          return { pools: 'one', pool: response.data.data[0] }
        } else {
          return { pools: 'multiple', pool: null }
        }
      })
  },

  loadRecentEvents (context) {
    const poolId = context.getters['getListenToPoolId']
    return ApiService
      .get(`history/events/pools/${poolId}/recent/hours/24`, { order_by: '!timestamp' })
      .then((response) => {
        context.commit('setEvents', response.data.data)
        return response
      })
  },

  loadRecentEventTotals (context) {
    const poolId = context.getters['getListenToPoolId']
    return ApiService
      .get(`history/events/pools/${poolId}/totals/hours/24`)
      .then((response) => {
        context.commit('setEventCounters', response.data.data)
        return response
      })
  }
}

export const mutations = {
  setListenToPool (state, pool) {
    state.data.listenToPool = pool
    state.data.listenToPoolId = pool.id
    state.data.listenToPoolName = pool.name
  },

  setListenToPoolId (state, poolId) {
    state.data.listenToPoolId = poolId
  },

  setListenToPoolName (state, poolName) {
    state.data.listenToPoolName = poolName
  },

  setListenToToken (state, token) {
    state.data.listenToToken = token
  },

  setMonitorMode (state, mode) {
    state.data.monitorMode = mode
  },

  handlePoolEvent (state, newEvent) {
    if (state.data.events.length > 100) {
      state.data.events.pop()
    }
    state.data.events.unshift(newEvent.data)

    if (newEvent.data.sound_id !== null && !state.data.soundMuted && !state.data.soundPlaying) {
      state.data.soundPlaying = true
      audioInstance = new Audio(require(`@/assets/sound/${newEvent.data.sound_id}.mp3`))

      audioInstance.onended = () => {
        state.data.soundPlaying = false
      }

      state.data.soundPlaying = true
      audioInstance.play()
    }

    const index = state.data.eventCounters.findIndex(event => event.event_code === newEvent.data.event_code)
    if (index > -1) {
      state.data.eventCounters[index].counter += 1
    } else {
      state.data.eventCounters.push({
        event_code: newEvent.data.event_code,
        counter: 1,
        color: newEvent.data.color
      })
    }
  },

  setEvents (state, events) {
    state.data.events = events
  },

  setEventCounters (state, eventCounters) {
    state.data.eventCounters = eventCounters
  },

  clearEvents (state) {
    state.data.events = []
    state.data.eventCounters = []
  },

  muteSound (state, muteSeconds) {
    state.data.soundMuted = true
    soundMuteTimeout = setTimeout(() => {
      state.data.soundMuted = false
    }, muteSeconds * 1000)
  },

  unmuteSound (state) {
    if (state.data.soundMuted) {
      // Cancel the timeout
      clearTimeout(soundMuteTimeout)
    }
    state.data.soundMuted = false
  },

  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data = Object.assign(defaults(), data)
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    state.data[name].push(value)
  },

  /**
   * removes an element from an array of a specific property
   * and replaced it with a new element.
   *
   * @param state   Object
   * @param name    String Name of property
   * @param index   Number The index of the element to remove
   * @param replacement New element to replace the old
   */
  spliceProp (state, { name, index, replacement }) {
    state.isModified = true
    state.data[name].splice(index, 1, replacement)
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    state.isModified = true
    utils.pullProp(state.data, name, index)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = Object.assign({}, defaults())
    state.form = Object.assign({}, formDefaults())
    state.isModified = false
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/events/monitoring/eventMonitoringModule.js